import type { PartnerConfig } from "../config";

// Set document-level metadata from the partner config. Called once at boot
// from src/index.js — synchronously, before React mounts — so the browser
// tab title and meta tags reflect the active partner before first paint.
//
// Tags are upserted: existing elements get their content rewritten, missing
// ones are created and appended to <head>. public/index.html is kept lean
// (just a generic <title> placeholder); this function owns the partner-aware
// metadata.
//
// Crawler caveat: this runs at runtime in JS. Static crawlers reading the
// HTML response see whatever is in public/index.html. If per-partner
// crawler-readable meta matters, generate per-partner index-{partnerId}.html
// at build time and have server.js serve the right one by Host header.
// Out of scope here.

export const applyDocumentMeta = (config: PartnerConfig): void => {
  const { meta } = config;
  // Resolve to an absolute URL — relative paths in og:image/twitter:image
  // make some social crawlers (older Slack, Twitter) fail to fetch the asset.
  const ogImageUrl = new URL(meta.ogImagePath, meta.canonicalUrl).toString();
  document.title = meta.title;
  setMeta("name", "description", meta.description);
  setMeta("name", "theme-color", meta.themeColor);
  setMeta("property", "og:type", "website");
  setMeta("property", "og:title", meta.title);
  setMeta("property", "og:description", meta.description);
  setMeta("property", "og:url", meta.canonicalUrl);
  setMeta("property", "og:image", ogImageUrl);
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", meta.title);
  setMeta("name", "twitter:description", meta.description);
  setMeta("name", "twitter:url", meta.canonicalUrl);
  setMeta("name", "twitter:image", ogImageUrl);
  setLink("canonical", meta.canonicalUrl);
};

const setMeta = (
  attr: "name" | "property",
  key: string,
  content: string,
): void => {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel: string, href: string): void => {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};
